/**
 * db-rn-ui Runtime Adapter
 *
 * 运行时适配器，支持以下场景：
 * 1. 独立使用：直接从 node_modules 导入
 * 2. 主-子应用架构：从主应用注入的全局变量获取
 *
 * 使用方式：
 * import { DbButton, DbTag } from 'db-rn-ui/runtime';
 */

// ============================================================================
// 运行时环境检测
// ============================================================================

const isBrowser = typeof window !== 'undefined';

// 检测是否从全局变量获取组件
const shouldUseGlobal = () => {
  // 生产环境且全局变量存在
  if (process.env.NODE_ENV === 'production') {
    const globalDbRnUI = (global as any).DbRnUI || (isBrowser && (window as any).DbRnUI);
    if (globalDbRnUI) {
      return true;
    }
  }
  return false;
};

// ============================================================================
// 获取组件来源
// ============================================================================

let DbRnUI: any = null;

if (shouldUseGlobal()) {
  // 从全局变量获取
  DbRnUI = (global as any).DbRnUI || (isBrowser && (window as any).DbRnUI);
  if (!DbRnUI) {
    console.warn('[db-rn-ui/runtime] global.DbRnUI not found, falling back to local import');
  }
}

// 如果全局不存在或开发环境，使用本地导入
if (!DbRnUI) {
  try {
    // 使用动态 require 避免循环依赖
    DbRnUI = require('../index');
  } catch (e) {
    console.error('[db-rn-ui/runtime] Failed to load db-rn-ui:', e);
    DbRnUI = {};
  }
}

// ============================================================================
// 辅助函数：安全获取组件
// ============================================================================

const safeGet = (name: string) => {
  const component = DbRnUI?.[name];
  if (!component && process.env.NODE_ENV === 'development') {
    console.warn(`[db-rn-ui/runtime] Component "${name}" not found`);
  }
  return component || (() => null);
};

// ============================================================================
// 基础组件
// ============================================================================

export const DbButton = safeGet('DbButton');
export const Button = safeGet('Button');
export const DbIcon = safeGet('DbIcon');

// ============================================================================
// 输入组件
// ============================================================================

export const DbNumberInput = safeGet('DbNumberInput');
export const DbInput = safeGet('DbInput');
export const DbCheckbox = safeGet('DbCheckbox');
export const DbCheckboxGroup = safeGet('DbCheckboxGroup');
export const DbRadio = safeGet('DbRadio');
export const DbRadioGroup = safeGet('DbRadioGroup');
export const DbSwitch = safeGet('DbSwitch');

// ============================================================================
// 展示组件
// ============================================================================

export const DbAmountDisplay = safeGet('DbAmountDisplay');
export const AmountDisplay = safeGet('AmountDisplay');
export const WaterfallFlow = safeGet('WaterfallFlow');
export const DbFlatList = safeGet('DbFlatList');
export const DbScrollList = safeGet('DbScrollList');
export const DbItemCard = safeGet('DbItemCard');
export const DbInfiniteList = safeGet('DbInfiniteList');
export const DbImagePreview = safeGet('DbImagePreview');
export const DbImageGallery = safeGet('DbImageGallery');
export const DbProductCard = safeGet('DbProductCard');
export const DbProductTitle = safeGet('DbProductTitle');
export const DbProductTitleInline = safeGet('DbProductTitleInline');
export const DbTag = safeGet('DbTag');
export const DbTagGroup = safeGet('DbTagGroup');
export const DbTitlePair = safeGet('DbTitlePair');
export const DbProgress = safeGet('DbProgress');
export const DbTrendArrow = safeGet('DbTrendArrow');
export const DbDisplay = safeGet('DbDisplay');
export const DbSwiper = safeGet('DbSwiper');

// ============================================================================
// 导航组件
// ============================================================================

export const DbNavBar = safeGet('DbNavBar');
export const DbCustomHeader = safeGet('DbCustomHeader');
export const CustomHeader = safeGet('CustomHeader');
export const DbScrollTab = safeGet('DbScrollTab');
export const DbTabView = safeGet('DbTabView');
export const DbTab = safeGet('DbTab');
export const DbTabList = safeGet('DbTabList');

// ============================================================================
// 布局组件
// ============================================================================

export const DbContainer = safeGet('DbContainer');
export const DbSection = safeGet('DbSection');
export const DbCard = safeGet('DbCard');
export const DbRow = safeGet('DbRow');
export const DbCol = safeGet('DbCol');
export const DbSpacer = safeGet('DbSpacer');
export const DbGridMenu = safeGet('DbGridMenu');

// ============================================================================
// 搜索组件
// ============================================================================

export const DbSearchBar = safeGet('DbSearchBar');
export const DbSearchHistory = safeGet('DbSearchHistory');

// ============================================================================
// 对话框组件
// ============================================================================

export const DbAlertDialog = safeGet('DbAlertDialog');
export const DbPopup = safeGet('DbPopup');
export const DbPopupField = safeGet('DbPopupField');
export const DbDrawer = safeGet('DbDrawer');

// ============================================================================
// 表单组件
// ============================================================================

export const DbSelectionButton = safeGet('DbSelectionButton');
export const DbSku = safeGet('DbSku');

// ============================================================================
// 业务页面组件
// ============================================================================

export const DbLoginPage = safeGet('DbLoginPage');
export const DbCartFooter = safeGet('DbCartFooter');
export const DbOrderBar = safeGet('DbOrderBar');
export const DbOrderCard = safeGet('DbOrderCard');
export const DbProductActionBar = safeGet('DbProductActionBar');

// ============================================================================
// 反馈组件
// ============================================================================

export const DbToast = safeGet('DbToast');
export const DbToastProvider = safeGet('DbToastProvider');
export const DbLoading = safeGet('DbLoading');
export const DbLoadingProvider = safeGet('DbLoadingProvider');
export const DbSwipeAction = safeGet('DbSwipeAction');

// ============================================================================
// Hooks
// ============================================================================

export const useDbTheme = safeGet('useDbTheme');

// ============================================================================
// 核心组件
// ============================================================================

export const DbThemeProvider = safeGet('DbThemeProvider');
export const DbContentPage = safeGet('DbContentPage');

// ============================================================================
// 动画组件 (如果存在)
// ============================================================================

export const FadeIn = safeGet('FadeIn');
export const SlideUp = safeGet('SlideUp');
export const Scale = safeGet('Scale');

// ============================================================================
// 导出类型
// ============================================================================

export * from './types';

// ============================================================================
// 导出原始模块（用于访问未显式导出的内容）
// ============================================================================

export { DbRnUI };

// ============================================================================
// 默认导出
// ============================================================================

export default DbRnUI;
